import React from "react";
import { Link } from "react-router-dom";
import { Heart, Users, Star, Sparkles, Shield, Globe, Baby, HandHeart, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import BottomNav from "@/components/BottomNav";

const AboutUs = () => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-purple-50 via-pink-50 to-white">
      {/* Hero Section */}
      <div className="relative bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 text-white py-20 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-10 left-10 w-32 h-32 bg-white rounded-full blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-40 h-40 bg-yellow-300 rounded-full blur-3xl"></div>
        </div>
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
            About KiddiPlay™
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Connecting Families. Building Community.
          </p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-12">
        {/* Vision Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center">
              <Star className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-purple-900" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Our Vision
            </h2>
          </div>
          <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl p-8">
            <p className="text-lg text-purple-900 leading-relaxed">
              To create a world where <strong>every family feels connected, supported and part of a thriving community</strong>.
            </p>
            <p className="text-purple-800 mt-4 leading-relaxed">
              We envision KiddiPlay becoming the trusted family lifestyle membership that brings people together, 
              helping children, parents, carers and communities build meaningful relationships, share experiences 
              and create lasting memories.
            </p>
          </div>
        </section>

        {/* Mission Section */}
        <section className="mb-16">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-pink-500 to-orange-500 rounded-2xl flex items-center justify-center">
              <Heart className="w-6 h-6 text-white" />
            </div>
            <h2 className="text-3xl font-bold text-purple-900" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Our Mission
            </h2>
          </div>
          <div className="bg-gradient-to-br from-pink-100 to-orange-100 rounded-2xl p-8">
            <p className="text-lg text-pink-900 leading-relaxed">
              To connect families through <strong>inspiring experiences, trusted resources and inclusive communities</strong> 
              that help every family learn, play, grow and belong.
            </p>
            <p className="text-pink-800 mt-4 leading-relaxed">
              KiddiPlay exists to reduce isolation, strengthen family relationships and make it easier for families 
              to discover opportunities to connect—with each other, with their local communities and with the world around them.
            </p>
            <p className="text-pink-900 mt-4 font-semibold text-xl italic">
              We believe every child deserves friendship, every parent deserves support, and every family deserves a village.
            </p>
            <p className="text-pink-800 mt-4 leading-relaxed">
              Through technology, partnerships and community, we are building that village—one family, one activity 
              and one meaningful connection at a time.
            </p>
          </div>
        </section>

        {/* Purpose & Promise */}
        <section className="mb-16 grid md:grid-cols-2 gap-6">
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-purple-100">
            <div className="flex items-center gap-3 mb-4">
              <Sparkles className="w-8 h-8 text-purple-500" />
              <h3 className="text-xl font-bold text-purple-900">Our Purpose</h3>
            </div>
            <p className="text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-500">
              Connecting Families.<br />Building Community.
            </p>
          </div>
          
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-pink-100">
            <div className="flex items-center gap-3 mb-4">
              <Shield className="w-8 h-8 text-pink-500" />
              <h3 className="text-xl font-bold text-pink-900">Our Promise</h3>
            </div>
            <p className="text-gray-700 leading-relaxed">
              We will create an inclusive, welcoming space where every family feels seen, valued and empowered 
              to make meaningful memories together—regardless of ability, background, culture, faith or circumstance.
            </p>
          </div>
        </section>

        {/* Our Belief */}
        <section className="mb-16">
          <div className="bg-gradient-to-r from-purple-600 via-pink-500 to-orange-400 rounded-2xl p-8 text-white text-center">
            <HandHeart className="w-16 h-16 mx-auto mb-4 opacity-90" />
            <h2 className="text-2xl font-bold mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>Our Belief</h2>
            <p className="text-xl font-medium">
              When families connect, children flourish.
            </p>
            <p className="text-xl font-medium">
              When communities connect, everyone thrives.
            </p>
          </div>
        </section>

        {/* The Problem */}
        <section className="mb-16">
          <h2 className="text-3xl font-bold text-purple-900 mb-6 text-center" style={{ fontFamily: "Fredoka, sans-serif" }}>
            The Challenge We're Solving
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {[
              { icon: Baby, text: "Children spend more time indoors" },
              { icon: Users, text: "Parents feel overwhelmed" },
              { icon: Heart, text: "Grandparents feel disconnected" },
              { icon: Shield, text: "SEND families often feel excluded" },
              { icon: Globe, text: "Communities don't know one another" },
              { icon: Star, text: "Local activities are difficult to discover" },
            ].map((item, idx) => (
              <div key={idx} className="flex items-center gap-4 bg-slate-100 rounded-xl p-4">
                <div className="w-10 h-10 bg-slate-200 rounded-full flex items-center justify-center flex-shrink-0">
                  <item.icon className="w-5 h-5 text-slate-500" />
                </div>
                <p className="text-slate-700">{item.text}</p>
              </div>
            ))}
          </div>
          <p className="text-center text-lg text-slate-600 mt-6">
            Parents want connection—but don't always know where to find it.
          </p>
        </section>

        {/* The Solution */}
        <section className="mb-16">
          <div className="bg-gradient-to-br from-green-100 to-teal-100 rounded-2xl p-8 text-center">
            <h2 className="text-3xl font-bold text-teal-900 mb-4" style={{ fontFamily: "Fredoka, sans-serif" }}>
              Now KiddiPlay helps reconnect the dots! 🧩
            </h2>
            <p className="text-teal-800 text-lg max-w-2xl mx-auto">
              We bring families together through playdates, events, shared resources, and a supportive community 
              that understands what modern parenting really needs.
            </p>
            <Link to="/register">
              <Button className="mt-6 bg-gradient-to-r from-purple-600 to-pink-500 text-white rounded-full px-8 py-6 text-lg">
                Join Our Community <ChevronRight className="ml-2" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Team / Niamh's Corner Teaser */}
        <section className="mb-16">
          <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-purple-100 flex flex-col md:flex-row items-center gap-6">
            <div className="w-28 h-28 md:w-32 md:h-32 rounded-full bg-gradient-to-br from-pink-400 via-purple-400 to-orange-300 p-1 flex-shrink-0">
              <div className="w-full h-full rounded-full bg-white overflow-hidden">
                <img 
                  src="https://customer-assets-lqy194kg.emergentagent.net/job_4791deb4-db22-4895-9e0a-21aa9f309cff/artifacts/jbpy90y1_Niamh%27s%20Cartoon%20Avatar%20v1.png"
                  alt="Niamh - Play CEO"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div>
              <h3 className="text-xl font-bold text-purple-900 mb-2">Meet Play CEO Niamh</h3>
              <p className="text-gray-700">
                Our youngest team member and Play CEO! Niamh shares her favourite books, activities, and 
                recommendations in her very own corner of KiddiPlay. Because who better to know what kids love?
              </p>
              <Link to="/niamhs-corner" className="inline-flex items-center gap-2 text-purple-600 font-medium mt-2 hover:text-purple-800">
                Visit Niamh's Corner <ChevronRight size={16} />
              </Link>
            </div>
          </div>
        </section>
      </div>

      <BottomNav />
    </div>
  );
};

export default AboutUs;
